import { zodResolver } from '@hookform/resolvers/zod';
import { Auth, signInWithEmailAndPassword } from 'firebase/auth';
import { useState } from 'react';
import { useForm } from 'react-hook-form';
import { z } from 'zod';

const loginSchema = z.object({
  email: z.string().email('Invalid email address'),
  password: z.string().min(8, 'Password must be at least 8 characters'),
});

type LoginFormData = z.infer<typeof loginSchema>;

interface UseLoginFormProps {
  auth: Auth;
  redirectUrl: string;
}

console.log('[DEBUG] Using shared/hooks/src/auth/useLoginForm.tsx');

export function useLoginForm({ auth, redirectUrl: _redirectUrl }: UseLoginFormProps) {
  const [isLoading, setIsLoading] = useState(false);
  const [progressMessage, setProgressMessage] = useState('');
  const [error, setError] = useState('');

  const {
    register,
    handleSubmit,
    formState: { errors },
  } = useForm<LoginFormData>({
    resolver: zodResolver(loginSchema),
  });

  const onSubmit = async (data: LoginFormData) => {
    console.log('[useLoginForm] Starting login process with data:', { ...data, password: '[REDACTED]' });
    setIsLoading(true);
    setProgressMessage('Signing you in, please wait...');
    setError('');
    try {
      if (!auth) {
        console.error('[useLoginForm] Firebase Auth instance is undefined!');
        setError('Internal error: Auth service unavailable. Please refresh and try again.');
        setIsLoading(false);
        setProgressMessage('');
        return;
      }
      if (!data.email || !data.password) {
        setError('Please enter both email and password.');
        setIsLoading(false);
        setProgressMessage('');
        return;
      }
      console.log('[useLoginForm] Attempting to sign in with Firebase Auth');
      const userCredential = await signInWithEmailAndPassword(auth, data.email, data.password);
      if (!userCredential || !userCredential.user) {
        console.error('[useLoginForm] No user returned from Firebase Auth:', userCredential);
        setError('No user returned from Firebase Auth. Please try again.');
        setIsLoading(false);
        setProgressMessage('');
        return;
      }
      console.log('[useLoginForm] Firebase Auth userCredential:', userCredential);
      setProgressMessage('Login successful!');

      // Do not handle navigation here. Let AuthProvider or Redirector manage post-login redirects.
      // setProgressMessage('Redirecting to your dashboard...');
      // Navigation is now centralized outside this hook.
    } catch (err: unknown) {
      console.error('[useLoginForm] Sign in error:', err);
      let message = 'An error occurred. Please try again.';
      if (err instanceof Error) {
        if ('code' in err) {
          if ((err as any).code === 'auth/user-not-found' || (err as any).code === 'auth/wrong-password') {
            message = 'Invalid email or password.';
          } else if ((err as any).code === 'auth/too-many-requests') {
            message = 'Too many login attempts. Your account has been temporarily locked for security reasons. Please wait and try again later. If you continue to have issues, contact your system administrator.';
            setError(message);
            setProgressMessage('');
            setIsLoading(false);
            return;
          } else if ((err as any).code === 'auth/network-request-failed') {
            message = 'Network error. Please check your connection and try again.';
          }
        }
        if ((err as any).code !== 'auth/too-many-requests') {
          message += ` [${err.message}]`;
        }
      }
      setError(message);
      setProgressMessage('');
    } finally {
      setIsLoading(false);
    }
  };

  return {
    register,
    handleSubmit,
    errors,
    onSubmit,
    isLoading,
    progressMessage,
    error,
  };
}
